<div class="tc-page">
  <div class="tc-save-bar">
    <div class="tc-save-bar__inner">
      <div class="tc-save-bar__left">
        <button
          type="button"
          class="tc-save-bar__back"
          data-testid="tc-back-arrow"
          [attr.aria-label]="'testCases.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tc-save-bar__heading">
          <div class="tc-save-bar__eyebrow-row">
            <span class="tc-save-bar__eyebrow">
              {{ (isEdit ? 'testCases.form.entityLabel' : 'testCases.form.newLabel') | translate }}
            </span>
            @if (isEdit && testCase()?.key; as key) {
              <code
                class="tc-save-bar__key"
                data-testid="key-pill"
                [attr.aria-label]="'testCases.form.keyAriaLabel' | translate: { key }"
              >
                {{ key }}
              </code>
            }
            @if (isEdit && linkedStoryKey()) {
              <span class="tc-source-badge" data-testid="source-badge">
                <span class="tc-source-badge__label">{{ 'testCases.form.source.label' | translate }}</span>
                <code class="tc-source-badge__value">
                  {{ 'testCases.form.source.ticketPrefix' | translate }} {{ linkedStoryKey() }}
                </code>
              </span>
            }
          </div>
          <span class="tc-save-bar__title">
            {{ (isEdit ? 'testCases.form.editTitle' : 'testCases.form.createTitle') | translate }}
          </span>
        </div>
        @if (form.dirty) {
          <span class="tc-dirty-pill">
            <span class="tc-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testCases.form.unsavedChangesBadge' | translate }}
          </span>
        }
      </div>
      <div class="tc-save-bar__right">
        @let missingRequiredFields = isReadOnly() ? [] : missingRequiredFieldNames();
        @if (missingRequiredFields.length) {
          <p id="tc-save-blocked-reason" class="tc-save-blocked" data-testid="tc-save-blocked-reason">
            <mat-icon class="tc-save-blocked__icon" aria-hidden="true">info</mat-icon>
            <span>
              {{ 'testCases.form.saveBlockedRequired' | translate }}
              {{ missingRequiredFields.join(', ') }}
            </span>
          </p>
        }
        @let incompleteSteps = isReadOnly() ? [] : incompleteConditionSteps();
        @if (incompleteSteps.length) {
          <p id="tc-save-blocked-condition" class="tc-save-blocked" data-testid="tc-save-blocked-condition">
            <mat-icon class="tc-save-blocked__icon" aria-hidden="true">info</mat-icon>
            <span>{{ 'testCases.form.saveBlockedCondition' | translate: { steps: incompleteSteps.join(', ') } }}</span>
          </p>
        }
        @if (isEdit && !isReadOnly()) {
          @if (automationCard(); as card) {
            <button
              mat-stroked-button
              type="button"
              data-testid="tc-run-btn"
              [disabled]="card.runBlocked() || startingManualRun()"
              (click)="card.openRunDialog()"
            >
              <mat-icon aria-hidden="true">play_arrow</mat-icon>
              {{ 'testCases.run.title' | translate }}
            </button>
          }
        }
        <button mat-stroked-button type="button" data-testid="tc-cancel-btn" (click)="onCancel()">
          {{ 'testCases.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="tc-save-btn"
            [disabled]="saveDisabled()"
            [attr.aria-describedby]="saveBlockedDescribedBy(!!missingRequiredFields.length, !!incompleteSteps.length)"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testCases.form.save' : 'testCases.form.saveCreate') | translate }}
          </button>
        }
      </div>
    </div>
    @if (manualRunError()) {
      <p class="tc-page__error tc-page__error--save" role="alert" data-testid="manual-run-error">
        {{ 'testCases.runError' | translate }}
      </p>
    }
  </div>

  @if (isReadOnly()) {
    <div class="tc-readonly-hint">
      <mat-icon class="tc-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="tc-readonly-hint__text">{{ 'testCases.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="tc-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="tc-page__error" role="alert">{{ 'testCases.form.loadError' | translate }}</p>
  } @else {
    <form class="tc-body" [formGroup]="form" (ngSubmit)="onSave()">
      <div class="tc-main">
        <div class="tc-card">
          <div class="tc-card__body">
            <to-section-title
              [title]="basicsSectionKeys.title | translate"
              [subtitle]="basicsSectionKeys.subtitle | translate"
            >
              @if (isEdit) {
                <span right data-testid="basics-right-slot">
                  @if (linkedStoryKey()) {
                    <button
                      type="button"
                      class="tc-source-refresh"
                      data-testid="source-refresh"
                      [disabled]="refreshing()"
                      (click)="refreshFromRequirement()"
                    >
                      <mat-icon aria-hidden="true">sync</mat-icon>
                      {{ 'testCases.form.source.refresh' | translate: { key: linkedStoryKey() } }}
                    </button>
                  }
                </span>
              }
            </to-section-title>

            @if (refreshError()) {
              <p class="tc-page__error" role="alert">{{ 'testCases.form.source.refreshError' | translate }}</p>
            }

            <div class="tc-basics-grid">
              <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                <mat-label>{{ 'testCases.form.title' | translate }}</mat-label>
                <input matInput formControlName="title" required [attr.maxlength]="fieldLengths.NAME_MAX" />
                @if (form.controls.title.touched && form.controls.title.errors?.['required']) {
                  <mat-error>{{ 'testCases.form.titleRequired' | translate }}</mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                <mat-label>{{ 'testCases.form.storyJiraKey' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="storyJiraKey"
                  [attr.maxlength]="fieldLengths.KEY_MAX"
                  [placeholder]="'testCases.form.storyJiraKeyPlaceholder' | translate"
                />
              </mat-form-field>

              <div class="tc-basics-grid__full">
                <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                  <mat-label>{{ 'testCases.form.description' | translate }}</mat-label>
                  <textarea
                    matInput
                    formControlName="description"
                    rows="3"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  ></textarea>
                  <mat-hint align="end" aria-live="polite">
                    {{ form.controls.description.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                  </mat-hint>
                </mat-form-field>
              </div>

              <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                <mat-label>{{ 'testCases.form.testGoal' | translate }}</mat-label>
                <input
                  matInput
                  formControlName="testGoal"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'testCases.form.testGoalPlaceholder' | translate"
                />
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.testGoal.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                </mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                <mat-label>{{ 'testCases.form.preconditions' | translate }}</mat-label>
                <input
                  #vorbedingungInput
                  matInput
                  formControlName="preconditions"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'testCases.form.preconditionsPlaceholder' | translate"
                />
                @if (kiVorbedingungBefund() || kiVorbedingungGeaendert()) {
                  <mat-hint class="tc-ki-marker" data-testid="ki-vorbedingung-marker">
                    @if (kiVorbedingungBefund()) {
                      <app-ki-marker art="befund" [hervorgehoben]="kiVorbedingungHervorgehoben()" />
                    }
                    @if (kiVorbedingungGeaendert()) {
                      <app-ki-marker art="geaendert" />
                    }
                  </mat-hint>
                }
              </mat-form-field>
            </div>
          </div>
        </div>

        @if (linkedRequirementId()) {
          <app-requirement-link-block
            [projectId]="projectId"
            [requirementId]="linkedRequirementId()"
          ></app-requirement-link-block>
        }

        @if (stepsReady()) {
          <app-test-step-table
            #stepTable
            [projectId]="projectId"
            [testCaseId]="testCaseId"
            [allowModuleEmbed]="true"
            [bufferedMode]="true"
            [initialSteps]="draftSteps()"
            [readOnly]="isReadOnly()"
            [availableSheets]="availableSheets()"
            [showStepKeys]="true"
            [kiBefundIds]="kiBefundSchrittIds()"
            [kiHervorgehobenId]="kiHervorgehobenSchrittId()"
            (stepsChanged)="onStepsChanged($event)"
            (moduleOpen)="onOpenModule($event)"
            (editModule)="onEditModule($event)"
          >
            <app-ki-qualitaets-check
              stepsCheck
              [projectId]="projectId"
              [testCaseId]="testCaseId"
              [titel]="kiTitel()"
              [vorbedingung]="kiVorbedingung()"
              [schritte]="draftSteps()"
              [anfrageId]="kiAnfrageId()"
              [readOnly]="isReadOnly()"
              (verbessern)="onKiVerbessern($event)"
              (hervorheben)="onKiHervorheben($event)"
            />
          </app-test-step-table>
        }

        <app-module-edit-drawer
          [projectId]="projectId"
          [moduleId]="editingModuleId()"
          [availableSheets]="availableSheets()"
          [contextStep]="editingContextStep()"
          [contextOverride]="editingContextOverride()"
          [readOnly]="isReadOnly()"
          (closed)="onModuleDrawerClosed()"
          (tdbOverrideApplied)="onTdbOverrideApplied($event)"
          (sheetsChanged)="availableSheets.set($event)"
        ></app-module-edit-drawer>

        @if (isEdit) {
          <app-version-diff-panel
            [projectId]="projectId"
            [testCaseId]="testCaseId"
            [current]="testCase()"
          ></app-version-diff-panel>
        }

        <!-- Details & Historie -->
        <div class="tc-card">
          <div class="tc-card__body">
            <to-section-title
              [title]="'testCases.form.sections.history.title' | translate"
              [subtitle]="'testCases.form.sections.history.subtitle' | translate"
            />
            @if (isEdit) {
              <app-test-case-handover-chain
                #handoverBlock
                [projectId]="projectId"
                [testCaseId]="testCaseId"
                [readonly]="isReadOnly()"
                [personOptions]="personOptions()"
                [currentUserId]="currentUserId()"
                (changed)="onHandoverChainChanged()"
                (handoverDraftChange)="onHandoverDraftChange($event)"
              />
              @if (handoverError()) {
                <p class="tc-handover-error" role="alert">{{ handoverError() }}</p>
              }
              <app-test-case-activity-feed
                [projectId]="projectId"
                [testCaseId]="testCaseId"
              ></app-test-case-activity-feed>
            }
            <div class="tc-notes">
              <mat-form-field appearance="outline" class="tc-field">
                <mat-label>{{ 'testCases.form.notes.label' | translate }}</mat-label>
                <textarea
                  matInput
                  formControlName="notes"
                  rows="3"
                  [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  [placeholder]="'testCases.form.notes.placeholder' | translate"
                ></textarea>
                <mat-hint align="end" aria-live="polite">
                  {{ form.controls.notes.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
                </mat-hint>
              </mat-form-field>
            </div>
          </div>
        </div>
      </div>

      <div class="tc-sidebar">
        <!-- Eigenschaften -->
        <div class="tc-card tc-properties">
          <div class="tc-card__body">
            <div class="tc-card__inline-header">{{ 'testCases.form.properties.blockTitle' | translate }}</div>

            <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
              <mat-label>{{ 'testCases.form.properties.type' | translate }}</mat-label>
              <mat-select formControlName="type" required>
                @for (type of typeOptions; track type) {
                  <mat-option [value]="type">{{ 'testCases.type.' + type | translate }}</mat-option>
                }
              </mat-select>
              @if (form.controls.type.touched && form.controls.type.errors?.['required']) {
                <mat-error>{{ 'testCases.form.properties.typeRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
              <mat-label>{{ 'testCases.form.properties.testType' | translate }}</mat-label>
              <mat-select formControlName="testType" required>
                @for (testType of testTypeOptions; track testType) {
                  <mat-option [value]="testType">{{ 'testCases.testType.' + testType | translate }}</mat-option>
                }
              </mat-select>
              @if (form.controls.testType.touched && form.controls.testType.errors?.['required']) {
                <mat-error>{{ 'testCases.form.properties.testTypeRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
              <mat-label>{{ 'testCases.form.properties.priority' | translate }}</mat-label>
              <mat-select formControlName="priority">
                @for (priority of priorityOptions; track priority) {
                  <mat-option [value]="priority">{{ 'testCases.priority.' + priority | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
              <mat-label>{{ 'testCases.form.properties.status' | translate }}</mat-label>
              <mat-select formControlName="status" required>
                @for (status of statusOptions; track status) {
                  <mat-option [value]="status">{{ 'testCases.status.' + status | translate }}</mat-option>
                }
              </mat-select>
              @if (form.controls.status.touched && form.controls.status.errors?.['required']) {
                <mat-error>{{ 'testCases.form.properties.statusRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <app-person-select
              class="tc-field"
              formControlName="assigneeUserId"
              [label]="'testCases.form.properties.assignee' | translate"
              [options]="personOptions()"
              [allowNone]="false"
              [required]="true"
              noneKey="testCases.form.properties.noAssignee"
            ></app-person-select>
            @if (form.controls.assigneeUserId.hasError('required') && form.controls.assigneeUserId.touched) {
              <p class="tc-field-error" role="alert">{{ 'testCases.form.properties.assigneeRequired' | translate }}</p>
            }

            <app-person-multi-select
              class="tc-field"
              formControlName="reviewerIds"
              [label]="'testCases.form.properties.reviewers' | translate"
              [options]="personOptions()"
              placeholderKey="testCases.form.properties.addReviewer"
              removeKey="testCases.form.properties.removeReviewer"
            ></app-person-multi-select>

            <mat-form-field appearance="outline" class="tc-field">
              <mat-label>{{ 'testCases.form.properties.version' | translate }}</mat-label>
              <input
                matInput
                readonly
                [value]="testCase()?.version ?? ('testCases.form.properties.versionEmpty' | translate)"
              />
            </mat-form-field>

            <mat-form-field appearance="outline" class="tc-field">
              <mat-label>{{ 'testCases.form.properties.tags' | translate }}</mat-label>
              <mat-chip-grid
                #tagGrid
                [disabled]="isReadOnly()"
                [attr.aria-label]="'testCases.form.properties.tagsAriaLabel' | translate"
              >
                @for (tag of form.controls.tags.value; track tag) {
                  <mat-chip-row [removable]="!isReadOnly()" (removed)="removeTag(tag)">
                    {{ tag }}
                    <button matChipRemove [attr.aria-label]="'testCases.form.properties.removeTag' | translate">
                      <mat-icon aria-hidden="true">cancel</mat-icon>
                    </button>
                  </mat-chip-row>
                }
              </mat-chip-grid>
              <input
                [matChipInputFor]="tagGrid"
                [disabled]="isReadOnly()"
                [placeholder]="'testCases.form.properties.tagsPlaceholder' | translate"
                (matChipInputTokenEnd)="addTag($event)"
              />
            </mat-form-field>

            @for (field of jiraTicketFields; track field.controlName) {
              <mat-form-field appearance="outline" class="tc-field">
                <mat-label>{{ field.labelKey | translate }}</mat-label>
                <input
                  matInput
                  [formControlName]="field.controlName"
                  [attr.maxlength]="fieldLengths.KEY_MAX"
                  [placeholder]="field.placeholderKey | translate"
                />
              </mat-form-field>
            }
          </div>
        </div>

        <app-test-case-automation-card
          #automationCard
          [statusControl]="form.controls.automationStatus"
          [frameworkControl]="form.controls.automationFramework"
          [locatorControl]="form.controls.automationLocator"
          [automationKey]="testCase()?.automationKey ?? null"
          [projectId]="projectId"
          [testCaseId]="testCaseId"
          [testCaseTitle]="form.controls.title.value ?? ''"
          [canWrite]="isEdit && !isReadOnly()"
          (runManually)="startManualRun()"
        ></app-test-case-automation-card>

        <app-test-case-execution-stats
          [projectId]="projectId"
          [testCaseId]="testCaseId"
        ></app-test-case-execution-stats>

        <app-test-case-links
          #linksBlock
          [projectId]="projectId"
          [testCaseId]="testCaseId"
          (openModalRequested)="showLinkModal.set(true)"
          (draftLinksChange)="onDraftLinksChanged($event)"
          (pendingDeletionsChange)="onPendingDeletionsChanged($event)"
        ></app-test-case-links>
      </div>

      @if (saveError()) {
        <p class="tc-page__error tc-page__error--save" role="alert">
          {{ saveErrorText() ?? ('testCases.form.saveError' | translate) }}
          @if (missingRequirementKey(); as key) {
            {{ 'testCases.form.links.requirementNotFound' | translate: { key } }}
          }
        </p>
      }
    </form>

    <app-add-link-modal
      [projectId]="projectId"
      [visible]="showLinkModal()"
      (visibleChange)="showLinkModal.set($event)"
      (added)="linksBlock.onAdd($event)"
    />
  }
</div>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="deactivateMessage() | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="deactivateConfirmLabel() | translate"
  [cancelText]="deactivateCancelLabel() | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
